<template>
  <div class="box">
    <div class="page">
      <div class="page-left">
        <div class="image-preview-section">
          <div class="title">参考图</div>
          <div class="image-preview-container">
            <img :src="imagePreview" alt="参考图预览" class="preview-image" v-if="imagePreview">
            <div class="no-image" v-else>
              <img class="placeholder-icon" src="../images/upload.png" alt="">
              <div class="placeholder-text">暂无参考图</div>
            </div>
          </div>
        </div>
        
        <div class="prompt-section">
          <div class="title mt16">门头名称</div>
          <div class="border2">
            <el-input 
              v-model="promptText" 
              maxlength="100" 
              style="width: 100%;height: 100%;"
              placeholder="请输入门头名称"
              show-word-limit 
              type="textarea" 
            />
          </div>
        </div>
        
        <div class="flex-col" @click="handleGenerateSameDesign" :class="{ 'loading': loading }">
          <div class="col-title" v-if="!loading">一键同款</div>
          <div class="col-title" v-else>生成中...</div>
          <div class="col-brief">（生成消耗10积分）</div>
        </div>
      </div>
    </div>
    <div class="page-right">
      <div class="title">生成记录</div>
      <div class="line"></div>
      <div class="active"></div>
      <div class="list" v-loading="historyLoading">
        <template v-if="designHistory.length > 0">
          <template v-for="(record, index) in designHistory" :key="record.id">
            <div class="item">
              <img class="item-logo" src="../images/logo.png" alt="">
              <div class="item-title">一键同款设计</div>
              <div class="item-time">{{ formatDate(record.created_at) }}</div>
              <div class="item-border">清晰度2k</div>
              <div class="item-border">比例3:4</div>
            </div>
            <img class="image"
              :src="record.result_image_url || 'https://mdn.alipayobjects.com/fecodex_image/afts/img/tn8-R60WTnEAAAAAR3AAAAgAejH3AQBr/original'" />

            <div class="row">
              <div class="d-box" @click="downloadDesign(record.id)">
                <img class="d-box-img" src="../images/d.png" alt="">
                <div class="d-box-title">下载</div>
              </div>
              <div class="d-box" @click="reeditDesign(record.id)">
                <img class="d-box-img" src="../images/edit.png" alt="">
                <div class="d-box-title">重新编辑</div>
              </div>
              <div class="d-box" @click="regenerateDesign(record.id)">
                <img class="d-box-img" src="../images/loading.png" alt="">
                <div class="d-box-title">重新生成</div>
              </div>
            </div>
          </template>
        </template>
        <div v-else class="no-history">
          <p>暂无历史记录</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';

// 获取store和provide
const store = useStore();
const route = useRoute();
const openLoginDialog = inject('openLoginDialog', null);

// 监听路由参数变化
onMounted(() => {
  // 检查是否有传入的图片URL
  if (route.query.imageUrl) {
    // 设置图片预览
    imagePreview.value = route.query.imageUrl;
    // 可以在这里添加其他处理逻辑
    console.log('接收到传入的图片URL:', route.query.imageUrl);
  }
});

// 页面逻辑
const imagePreview = ref('');
const promptText = ref('');

// Loading状态
const loading = ref(false);
const historyLoading = ref(false);

// 生成记录
const designHistory = ref([]);

// 检查用户是否已登录
const checkLogin = async () => {
  if (!store.getters.isLoggedIn) {
    // 用户未登录，显示提示
    ElMessage({
      message: '暂未登录',
      type: 'warning',
      duration: 3000
    });
    
    // 打开登录弹窗
    if (openLoginDialog) {
      openLoginDialog('login');
    }
    return false;
  }
  return true;
}

// 获取一键同款生成记录
const fetchDesignHistory = async () => {
  historyLoading.value = true;
  try {
    const params = {
      limit: 10,
      offset: 0
    };
    const response = await store.dispatch('getSameDesignHistory', params);
    const newData = response.data.data?.history || [];
    
    // 保留现有记录中的原始参数
    const existingRecordsMap = {};
    designHistory.value.forEach(record => {
      if (record.original_params) {
        existingRecordsMap[record.id] = record.original_params;
      }
    });
    
    // 将原始参数添加到新数据中
    newData.forEach(record => {
      if (existingRecordsMap[record.id]) {
        record.original_params = existingRecordsMap[record.id];
      }
    });
    
    designHistory.value = newData;
  } catch (error) {
    console.error('获取一键同款生成记录失败:', error);
    
    // 检查是否是因为用户未登录导致的错误
    if (error.status === 500) {
      ElMessage({
        message: '请先登录以查看生成记录',
        type: 'warning',
        duration: 3000
      });
    }
    
    designHistory.value = [];
  } finally {
    historyLoading.value = false;
  }
}

// 组件挂载时获取生成记录
onMounted(() => {
  fetchDesignHistory();
})

// 监听用户登录状态变化
watch(
  () => store.getters.isLoggedIn,
  (newVal, oldVal) => {
    // 如果用户刚登录（从false变为true），则获取生成记录
    if (newVal && !oldVal) {
      fetchDesignHistory();
    }
  }
);

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '未知时间';
  
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`;
}



// 一键同款生成
const handleGenerateSameDesign = async () => {
  if (!(await checkLogin())) return;
  

  
  // 检查积分是否足够（假设生成需要10积分）
  const requiredPoints = 10;
  const currentPoints = store.getters.userInfo?.points || store.getters.userInfo?.credit || 0;
  
  if (currentPoints < requiredPoints) {
    ElMessage({
      message: '积分不足，请先充值',
      type: 'warning',
      duration: 3000
    });
    return;
  }
  
  // 设置loading状态
  loading.value = true;
  
  try {
    // 构造请求数据
    const requestData = {
      imageUrl: imagePreview.value,
      description: promptText.value || '', // 门头名称
      return_url: true,
      width: 1024,
      height: 1024,
    };
    
    // 调用store action生成设计
    // 注意：超时控制已在 axios 配置中设置（30分钟），这里不再单独设置
    const response = await store.dispatch('generateSameDesign', requestData);
    
    console.log('一键同款设计生成成功:', response);
    
    // 生成成功后，重新获取用户信息以更新积分
    try {
      await store.dispatch('getCurrentUser');
    } catch (error) {
      console.error('更新用户信息失败:', error);
      
      // 检查是否是因为用户未登录导致的错误
      if (error.message === '用户未登录') {
        ElMessage({
          message: '请先登录以获取用户信息',
          type: 'warning',
          duration: 3000
        });
      }
    }
    
    // 生成成功后，重新获取一键同款生成记录
    try {
      await fetchDesignHistory();
    } catch (error) {
      console.error('更新一键同款生成记录失败:', error);
    }
    
  } catch (error) {
    console.error('一键同款设计生成失败:', error);
    
    // 使用拦截器设置的友好错误提示，或根据错误类型显示默认提示
    let errorMessage = error.friendlyMessage || '生成设计失败，请稍后重试';
    
    ElMessage({
      message: errorMessage,
      type: 'error',
      duration: 3000
    });
  } finally {
    // 重置loading状态
    loading.value = false;
  }
}

// 下载设计作品
const downloadDesign = (designId) => {
  // 找到对应的设计记录
  const record = designHistory.value.find(item => item.id === designId);
  if (!record || !record.result_image_url) {
    ElMessage({
      message: '找不到设计作品或图片链接',
      type: 'error',
      duration: 3000
    });
    return;
  }
  
  try {
    // 直接下载图片到本地
    const link = document.createElement('a');
    link.href = record.result_image_url;
    link.setAttribute('download', `same-design-${designId}.png`);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    ElMessage({
      message: '下载成功',
      type: 'success',
      duration: 3000
    });
  } catch (error) {
    console.error('下载失败:', error);
    ElMessage({
      message: '下载失败: ' + (error.message || '未知错误'),
      type: 'error',
      duration: 3000
    });
  }
}

// 重新编辑设计
const reeditDesign = async (designId) => {
  if (!(await checkLogin())) return;
  
  try {
    // 显示加载状态
    historyLoading.value = true;
    
    // 找到对应的记录
    const record = designHistory.value.find(item => item.id === designId);
    if (!record) {
      ElMessage({
        message: '找不到对应的记录',
        type: 'error',
        duration: 3000
      });
      return;
    }
    
    // 直接使用历史记录中的参数填充表单
    // 填充提示文本
    promptText.value = record.description || '';
    
    ElMessage({
      message: '已加载历史记录到表单',
      type: 'success',
      duration: 3000
    });
    
  } catch (error) {
    console.error('重新编辑失败:', error);
    ElMessage({
      message: '重新编辑失败: ' + (error.response?.data?.message || error.message || '未知错误'),
      type: 'error',
      duration: 3000
    });
  } finally {
    historyLoading.value = false;
  }
}

// 重新生成设计
const regenerateDesign = async (designId) => {
  if (!(await checkLogin())) return;
  
  try {
    // 显示加载状态
    historyLoading.value = true;
    
    // 找到对应的记录
    const record = designHistory.value.find(item => item.id === designId);
    if (!record) {
      ElMessage({
        message: '找不到对应的记录',
        type: 'error',
        duration: 3000
      });
      return;
    }
    
    // 使用立即生成的接口重新生成
    // 构造与立即生成完全相同的参数
    const requestData = {
      imageUrl: record.image_url || '',
      description: record.description || '', // 门头名称
    };
    
    console.log('使用重构参数重新生成:', requestData);
    
    // 调用store action生成设计
    const response = await store.dispatch('generateSameDesign', requestData);
    
    console.log('一键同款重新生成成功:', response);
    
    // 生成成功后，重新获取用户信息以更新积分
    try {
      await store.dispatch('getCurrentUser');
    } catch (error) {
      console.error('更新用户信息失败:', error);
      
      // 检查是否是因为用户未登录导致的错误
      if (error.message === '用户未登录') {
        ElMessage({
          message: '请先登录以获取用户信息',
          type: 'warning',
          duration: 3000
        });
      }
    }
    
    // 生成成功后，重新获取一键同款生成记录
    try {
      await fetchDesignHistory();
    } catch (error) {
      console.error('更新一键同款生成记录失败:', error);
    }
    
  } catch (error) {
    console.error('重新生成失败:', error);
    ElMessage({
      message: '重新生成失败: ' + (error.response?.data?.message || error.message || '未知错误'),
      type: 'error',
      duration: 3000
    });
  } finally {
    historyLoading.value = false;
  }
}
</script>

<style scoped>
.box {
  display: flex;
  align-items: center;
}

.page {
  width: 490px;
  height: 630px;
  padding: 20px;
  box-sizing: border-box;
  background: #1D1D1D;
  border-radius: 16px 16px 16px 16px;
}

.page-left {
  width: 100%;
}

.title {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 16px;
  color: #FFFFFF;
}

.mt16 {
  margin-top: 16px;
}

.image-upload-section {
  width: 100%;
}

.image-upload-area {
  width: 100%;
  height: 200px;
  background: #2B2C2C;
  border-radius: 8px;
  border: 1px dashed #6A6B6E;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 10px;
}

.upload-icon {
  width: 48px;
  height: 48px;
}

.upload-text {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #FFFFFF;
  margin-top: 12px;
}

.upload-hint {
  font-family: PingFang SC, PingFang SC;
  font-weight: normal;
  font-size: 12px;
  color: #6A6B6E;
  margin-top: 8px;
  text-align: center;
}

.preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
}

.change-image {
  font-family: PingFang SC, PingFang SC;
  font-weight: normal;
  font-size: 12px;
  color: #5EFF00;
  margin-top: 10px;
}

.prompt-section {
  width: 100%;
}

.border2 {
  width: 100%;
  height: 80px;
  background: #2B2C2C;
  border-radius: 8px 8px 8px 8px;
  border: 1px solid #2B2C2C;
  margin-top: 10px;
}

.flex-col {
  width: 100%;
  height: 60px;
  background: #5EFF00;
  border-radius: 8px 8px 8px 8px;
  border: 1px solid #00FBFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  cursor: pointer;
}

.flex-col.loading {
  background: #cccccc;
  border-color: #999999;
  cursor: not-allowed;
}

.col-title {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 16px;
  color: #222222;
}

.col-brief {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #328800;
}

.page-right {
  flex: 1;
  margin-left: 16px;
  height: 630px;
}

.line {
  width: 100%;
  margin-top: 16px;
  height: 0px;
  border: 1px solid #2B2C2C;
}

.active {
  width: 64px;
  height: 0px;
  border: 3px solid #5EFF00;
  font-size: 16px;
  margin-top: -6px;
}

.list{
  height: 600px;
  overflow-y: scroll;
}

.no-history {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6A6B6E;
  font-size: 16px;
  text-align: center;
}

.item {
  display: flex;
  align-items: center;
  margin-top: 20px;
}

.item-logo {
  width: 24px;
  height: 24px;
}

.item-title {
  margin-left: 12px;
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #FFFFFF;
}

.item-time {
  margin-left: 12px;
  font-family: Arial, Arial;
  font-weight: 400;
  font-size: 14px;
  color: #6A6B6E;
}

.item-border {
  margin-left: 12px;
  width: 59px;
  height: 22px;
  background: #2B2C2C;
  border-radius: 3px 3px 3px 3px;
  border: 1px solid #2B2C2C;
  font-family: Arial, Arial;
  font-weight: 400;
  font-size: 12px;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row {
  display: flex;
  align-items: center;
}

.d-box {
  width: 112px;
  height: 32px;
  background: #17181A;
  border-radius: 8px 8px 8px 8px;
  border: 1px solid #2B2C2C;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
  margin-left: 8px;
}

.d-box:first-child {
  margin-left: 0px;
}

.d-box-img {
  width: 16px;
  height: 16px;
}

.d-box-title {
  font-family: Arial, Arial;
  font-weight: normal;
  font-size: 14px;
  color: #FFFFFF;
  margin-left: 8px;
}

.image {
  height: 351px;
  width: 630px;
  border-radius: 8px 8px 8px 8px;
  margin: 26px 0 0 38px;
}

/* 自定义 Element Plus 变量 */
.page-left {
  --el-fill-color-blank: #2B2C2C;
  --el-bg-color-overlay: #2B2C2C;
  --el-border-color: none;
  --el-text-color-regular: #fff;
  --el-border-color-hover: #555;
  --el-color-primary: #5EFF00;
  --el-fill-color-light: #666;
}

.border2 :deep(.el-input__wrapper) {
  background-color: var(--el-fill-color-blank) !important;
  border: none !important;
}

.border2 :deep(.el-input__inner) {
  color: var(--el-text-color-regular) !important;
}

/* 垂直滚动条 */
::-webkit-scrollbar {
  width: 12px;
  /* 垂直滚动条宽度 */
  height: 12px;
  /* 水平滚动条高度 */
}

/* 滚动条轨道 */
::-webkit-scrollbar-track {
  background: #090909;
  border-radius: 6px;
}

/* 滚动条滑块 */
::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #121212, #939292);
  border-radius: 6px;
}

/* 滑块悬停效果 */
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #666, #444);
}

/* 水平滚动条滑块 */
::-webkit-scrollbar-thumb:horizontal {
  background: linear-gradient(90deg, #000000, #000000);
}

/* 角落 */
::-webkit-scrollbar-corner {
  background: #000000;
}

/* 参考图预览样式 */
.image-preview-section {
  margin-bottom: 20px;
}

.image-preview-container {
  border: 2px dashed #333;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
}

.no-image {
  color: #666;
}

.placeholder-icon {
  width: 48px;
  height: 48px;
  opacity: 0.5;
  margin-bottom: 10px;
}

.placeholder-text {
  font-size: 14px;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .box {
    flex-direction: column;
  }
  
  .page {
    width: 100%;
    max-width: 490px;
    height: auto;
    margin-bottom: 16px;
  }
  
  .page-right {
    width: 100%;
    margin-left: 0;
    height: auto;
  }
  
  .list {
    height: 400px;
  }
  
  .image {
    width: 100%;
    height: auto;
    max-width: 100%;
    margin: 16px 0 0 0;
  }
  
  .d-box {
    flex: 1;
    min-width: 80px;
    margin-left: 4px;
    margin-right: 4px;
  }
  
  .d-box:first-child {
    margin-left: 0;
  }
  
  .row {
    flex-wrap: wrap;
  }
  
  .image-preview-container {
    min-height: 150px;
    padding: 10px;
  }
  
  .preview-image {
    max-height: 200px;
  }
}
</style>